import React, { forwardRef, useState } from 'react';

import {
	CheckboxOption,
	type InputActionMeta,
	type OptionType,
	PopupSelect,
	components as selectComponents,
} from '@atlaskit/select';

import { type GenericSelectProps } from '../types';

import { Control } from './control';
import { MenuList } from './menu-list';
import { NoOptionsMessage } from './no-options-message';

export const GenericSelect = forwardRef<PopupSelect<OptionType, true>, GenericSelectProps>(
	(
		{
			onChange,
			onRetry,
			onShowMore,
			onInputChange,
			options,
			value,
			isError,
			isLoading,
			isPaginationLoading,
			components,
			target,
			footer,
			searchThreshold = 5,
			placeholder,
			hasNextPage,
			onClose,
			isInvalid,
			isDisabled,
			formatOptionLabel,
			onMenuOpen,
			isMulti = true,
			closeMenuOnSelect = !isMulti,
			defaultIsOpen,
			filterOption,
			'aria-label': ariaLabel,
			ariaLiveMessages,
			shouldRenderToParent = false,
			onKeyDown,
		},
		ref,
	) => {
		const [inputValue, setInputValue] = useState('');

		const handleInputChange = (newValue: string, meta: InputActionMeta) => {
			const filteredActions = ['set-value', 'input-blur', 'menu-close'];

			if (filteredActions.includes(meta.action) && !newValue.length) {
				return;
			}

			setInputValue(newValue);

			if (onInputChange) {
				onInputChange(newValue, meta);
			}
		};

		const handleClose = () => {
			setInputValue('');

			if (onClose) {
				onClose();
			}
		};

		// To render the popup next to the trigger element to achieve DOM order
		const popupStrategy = shouldRenderToParent ? 'fixed' : 'absolute';

		const getOptionComponent = () => {
			return isMulti ? CheckboxOption : selectComponents.Option;
		};

		return (
			<PopupSelect
				ref={ref}
				autoFocus
				isMulti={isMulti}
				onKeyDown={onKeyDown}
				defaultIsOpen={defaultIsOpen}
				searchThreshold={searchThreshold}
				inputValue={inputValue}
				placeholder={placeholder}
				hideSelectedOptions={false}
				controlShouldRenderValue={false}
				backspaceRemovesValue={false}
				closeMenuOnSelect={closeMenuOnSelect}
				cacheOptions={false}
				blurInputOnSelect={false}
				options={options}
				isClearable={false}
				onChange={onChange}
				value={value}
				minMenuWidth={298}
				onInputChange={handleInputChange}
				filterOption={filterOption}
				label={ariaLabel}
				ariaLiveMessages={ariaLiveMessages}
				target={target}
				formatOptionLabel={formatOptionLabel}
				shouldCloseMenuOnTab={false}
				components={{
					Option: getOptionComponent(),
					Control,
					MenuList,
					NoOptionsMessage,
					...components,
				}}
				footer={footer}
				onClose={handleClose}
				onMenuOpen={onMenuOpen}
				isInvalid={isInvalid}
				isDisabled={isDisabled}
				isLoading={isLoading}
				isPaginationLoading={isPaginationLoading}
				hasNextPage={hasNextPage}
				isError={isError}
				onRetry={onRetry}
				onShowMore={onShowMore}
				popperProps={{
					strategy: popupStrategy,
				}}
			/>
		);
	},
) as <Option extends OptionType, IsMulti extends boolean>(
	props: GenericSelectProps<Option, IsMulti> & {
		ref?: React.Ref<PopupSelect<Option, IsMulti>>;
	},
) => React.ReactElement;
